import { TextKey, RouteName } from '@mews-commander/enums';
import { useLocalizeText } from '@mews/framework';
import { UnicodeEntity } from '@mews/enums';
import {
    hasCurrentEnterpriseServiceId,
    getCurrentEnterpriseChainId,
} from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { areCurrentEnterpriseCustomerStatisticsEnabled } from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import {
    canCurrentProfileManageTravelAgencies,
    canCurrentProfileManageCustomersAndCompanies,
} from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { useCommanderSelector } from '@mews-commander/utils';
import { AssistantNavigation } from './AssistantNavigation';
import { useNavigationSearchResult } from './useNavigationSearchResult';
import { useNavigationPath } from './useNavigationPath';

export const useProfilesNavigation = (): AssistantNavigation[] => {
    const chainId = useCommanderSelector<string>(getCurrentEnterpriseChainId);
    const isCustomerManager = useCommanderSelector<boolean>(canCurrentProfileManageCustomersAndCompanies);
    const customerStatisticsEnabled = useCommanderSelector<boolean>(areCurrentEnterpriseCustomerStatisticsEnabled);
    const hasReservableService = useCommanderSelector<boolean>(hasCurrentEnterpriseServiceId);
    const canManageCustomersAndCompanies = useCommanderSelector<boolean>(canCurrentProfileManageCustomersAndCompanies);
    const canManageTravelAgencies = useCommanderSelector<boolean>(canCurrentProfileManageTravelAgencies);

    const localizeText = useLocalizeText();

    const parentPath = `${localizeText({ textKey: TextKey.ProfilePlural })}${UnicodeEntity.Space}${
        UnicodeEntity.ForwardSlash
    }${UnicodeEntity.Space}`;

    const customerCreate = useNavigationSearchResult(
        RouteName.CustomerCreate,
        TextKey.Customer,
        {
            query: { RedirectToProfile: 'true', ChainId: chainId },
        },
        useNavigationPath(parentPath, TextKey.Customer)
    );
    const customerStatisticReport = useNavigationSearchResult(
        RouteName.CustomerStatisticReportIndex,
        TextKey.CustomerStatisticReport,
        {},
        useNavigationPath(parentPath, TextKey.CustomerStatisticReport)
    );
    const customerProfileReport = useNavigationSearchResult(
        RouteName.CustomerProfileReportIndex,
        TextKey.CustomerProfileReport,
        {},
        useNavigationPath(parentPath, TextKey.CustomerProfileReport)
    );
    const companyReport = useNavigationSearchResult(
        RouteName.CompanyReportIndex,
        TextKey.CompanyPlural,
        {},
        useNavigationPath(parentPath, TextKey.CompanyPlural)
    );
    const travelAgencyContractReport = useNavigationSearchResult(
        RouteName.TravelAgencyContractReportIndex,
        TextKey.TravelAgencyContractPlural,
        {},
        useNavigationPath(parentPath, TextKey.TravelAgencyContractPlural)
    );
    const timeSpentReport = useNavigationSearchResult(
        RouteName.TimeSpentReportIndex,
        TextKey.TimeSpentReport,
        {},
        useNavigationPath(parentPath, TextKey.TimeSpentReport)
    );

    return [
        isCustomerManager ? customerCreate : null,
        isCustomerManager && customerStatisticsEnabled ? customerStatisticReport : null,
        isCustomerManager && hasReservableService ? customerProfileReport : null,
        canManageCustomersAndCompanies ? companyReport : null,
        canManageTravelAgencies && hasReservableService ? travelAgencyContractReport : null,
        hasReservableService && isCustomerManager ? timeSpentReport : null,
    ].filter(Boolean) as AssistantNavigation[];
};
